import React, { useState, useEffect } from 'react';

/**
 * Fullscreen Image Lightbox with 4K Synology NAS Volume3 Toggle
 */
export default function RiskImageLightbox({
  imageItem = null,
  imageUrl = null,
  isOpen = false,
  onClose
}) {
  const [view4k, setView4k] = useState(false);

  // Reset 4K toggle when imageItem changes
  useEffect(() => {
    setView4k(false);
  }, [imageItem, imageUrl]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || (!imageItem && !imageUrl)) return null;

  const imgObj = typeof imageItem === 'string'
    ? { full_url: imageItem, thumb_url: imageItem, filename: 'ภาพถ่ายหลักฐาน' }
    : imageItem || {};

  const fullUrl = imgObj.full_url || imgObj.url || imageUrl;
  const thumbUrl = imgObj.thumb_url || imgObj.url || imageUrl;
  const filename = imgObj.filename || 'ภาพถ่ายหลักฐานอุบัติการณ์';
  const hasFullUrl = !!fullUrl;

  const currentSrc = view4k && hasFullUrl ? fullUrl : (thumbUrl || fullUrl);

  return (
    <div
      className="risk-lightbox-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image Preview"
    >
      <div
        className="risk-lightbox-box"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '92vw',
          maxHeight: '88vh',
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: '#0f172a',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header bar with 4K toggle and close */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 16px',
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ color: '#f8fafc', fontSize: '0.88em', fontWeight: '700' }}>
            {filename}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {hasFullUrl && (
              <button
                type="button"
                onClick={() => setView4k(!view4k)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  backgroundColor: view4k ? '#0d9488' : 'rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  border: view4k ? '1.5px solid #14b8a6' : '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '8px',
                  fontSize: '0.82em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{view4k ? '🔄 สลับดู Thumbnail' : '🔍 ดูภาพความละเอียดสูงระดับ 4K (จาก NAS volume3)'}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: '#ffffff',
                border: 'none',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="ปิดหน้าต่าง (Esc)"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Image Display */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000000',
            padding: '8px',
            overflow: 'auto',
            maxHeight: 'calc(88vh - 54px)'
          }}
        >
          <img
            src={currentSrc}
            alt={filename}
            style={{
              maxWidth: '90vw',
              maxHeight: 'calc(85vh - 70px)',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>

        {/* Footer info bar */}
        <div
          style={{
            padding: '6px 16px',
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.75em',
            color: '#94a3b8'
          }}
        >
          <span>
            {view4k
              ? '⚡ ความละเอียดต้นฉบับ 4K (จัดเก็บบน On-Prem Synology NAS volume3)'
              : '⚡ ภาพตัวอย่าง Thumbnail รวดเร็ว'}
          </span>
          {hasFullUrl && (
            <a
              href={fullUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: '600' }}
            >
              เปิดภาพเต็มในแท็บใหม่ ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
